import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '@/utils/api';
import type { AITrackData } from '@/components/ai/AITrackPreviewModal';
import { parseStreamingTrackData } from '@/utils/incrementalJsonParser';

// Types
export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  imageUrl?: string;
  modelUsed?: string;
  responseTimeMs?: number;
  createdAt: string;
}

export interface AIConversation {
  id: string;
  title: string;
  conversationType: 'general' | 'plan' | 'image_parse' | 'command';
  messageCount: number;
  lastMessageAt?: string;
  isArchived: boolean;
  createdAt: string;
  lastMessage?: string;
}

export interface AIPlan {
  id: string;
  title: string;
  description?: string;
  goalPrompt: string;
  planType: 'learning' | 'career' | 'exam' | 'project' | 'habit' | 'custom';
  status: 'draft' | 'active' | 'paused' | 'completed' | 'deleted';
  storagePreference: 'tracks' | 'planner' | 'ask';
  autoCreate: boolean;
  showPreview: boolean;
  startDate?: string;
  targetEndDate?: string;
  progressPercentage: number;
  totalTasks: number;
  completedTasks: number;
  lastFineTunedAt?: string;
  createdAt: string;
}

export interface AIPlanTask {
  id: string;
  planId: string;
  title: string;
  description?: string;
  weekNumber: number;
  dayOffset: number;
  scheduledDate?: string;
  estimatedMinutes: number;
  difficulty: 'easy' | 'medium' | 'hard';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  taskType: 'learn' | 'practice' | 'review' | 'build' | 'read' | 'watch';
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'deferred';
  isCreated: boolean;
  resources?: string[];
  sortOrder: number;
}

export interface ParsedCourseStructure {
  title: string;
  description?: string;
  sections: Array<{
    name: string;
    items: Array<{
      title: string;
      type: string;
      duration?: string;
      estimatedMinutes?: number;
    }>;
    estimatedMinutes?: number;
  }>;
  totalItems: number;
  estimatedHours?: number;
}

export interface AIUserPreferences {
  defaultStorage: 'tracks' | 'planner' | 'ask';
  autoCreateTracks: boolean;
  showPreview: boolean;
  dailySuggestionTime: string;
  enableFineTuning: boolean;
  fineTuneFrequency: 'daily' | 'weekly' | 'manual';
  preferredModel: string;
  visionModel: string;
  enableSoundEffects: boolean;
  enableHapticFeedback: boolean;
}

export interface AIInsights {
  performance: {
    completedToday: number;
    totalToday: number;
    completedThisWeek: number;
    totalThisWeek: number;
    averageCompletionRate: number;
    averageTimePerTask: number;
    currentStreak: number;
  };
  tracks: Array<{
    name: string;
    progress: number;
    color: string;
  }>;
  insights: string[];
  recentActivity: string[];
}

export interface AIHealthStatus {
  status: 'healthy' | 'unavailable' | 'error';
  ollama: {
    connected: boolean;
    models: string[];
  };
}

export interface ProactiveSuggestion {
  type: 'streak_risk' | 'overdue' | 'stagnant' | 'burnout' | 'revision_due' | 'milestone' | 'celebration' | 'tip';
  severity: 'info' | 'warning' | 'urgent';
  title: string;
  description: string;
  actionLabel?: string;
  actionData?: any;
}

export interface DailyBriefing {
  todayPlan: Array<{
    id: string;
    title: string;
    entityType: string;
    entityId: string;
    trackName: string | null;
    status: string;
    scheduledDate: string;
    estimatedMinutes: number | null;
    difficulty: string | null;
  }>;
  streaksAtRisk: Array<{
    id: string;
    name: string;
    currentStreak: number;
  }>;
  overdueCount: number;
  yesterdayWins: string[];
  suggestions: ProactiveSuggestion[];
  performance: {
    completedYesterday: number;
    totalYesterday: number;
    currentStreak: number;
  };
}

export interface TrackGenerationParams {
  timeAvailable: '2-weeks' | '1-month' | '2-months' | '3-months' | '6-months' | '1-year';
  currentLevel: 'beginner' | 'intermediate' | 'advanced';
  tasksPerDay: number;
}

interface AIState {
  // Health
  health: AIHealthStatus | null;

  // Chat
  isOpen: boolean;
  currentConversationId: string | null;
  conversations: AIConversation[];
  messages: AIMessage[];
  isTyping: boolean;
  streamingContent: string;

  // Streaming track preview (for incremental UI)
  streamingTrackData: AITrackData | null;
  isStreamingTrack: boolean;

  // Plans
  plans: AIPlan[];
  currentPlan: AIPlan | null;
  planTasks: AIPlanTask[];

  // Parsed content
  parsedContent: ParsedCourseStructure | null;
  parsedContentId: string | null;

  // Preferences
  preferences: AIUserPreferences | null;

  // Insights
  insights: AIInsights | null;

  // Action preview (for user confirmation before creating)
  actionPreview: {
    type: 'create_track' | 'schedule_tasks';
    data: any;
  } | null;

  // Action results
  lastActionResult: {
    action: string;
    success: boolean;
    message?: string;
    trackId?: string;
    trackName?: string;
    patternsCreated?: number;
    cardsCreated?: number;
    scheduled?: number;
  } | null;

  // Track generation questionnaire
  showQuestionnaire: boolean;
  pendingMessage: string | null;
  trackGenerationParams: TrackGenerationParams | null;

  // Proactive suggestions
  suggestions: ProactiveSuggestion[];
  suggestionCount: number;

  // Daily briefing
  dailyBriefing: DailyBriefing | null;
  showDailyBriefing: boolean;
  briefingDismissedDate: string | null;

  // Content generation
  generatedFlashcards: Array<{ front: string; back: string }> | null;
  generatedQuiz: Array<{ question: string; options: string[]; correctIndex: number; explanation: string }> | null;
  generatedStudyGuide: { content: string; format: string } | null;
  isGeneratingContent: boolean;

  // Loading states
  isLoading: boolean;
  isSending: boolean;
  isParsing: boolean;
  isCreatingPlan: boolean;
  error: string | null;
}

const initialState: AIState = {
  health: null,
  isOpen: false,
  currentConversationId: null,
  conversations: [],
  messages: [],
  isTyping: false,
  streamingContent: '',
  streamingTrackData: null,
  isStreamingTrack: false,
  plans: [],
  currentPlan: null,
  planTasks: [],
  parsedContent: null,
  parsedContentId: null,
  preferences: null,
  insights: null,
  actionPreview: null,
  lastActionResult: null,
  showQuestionnaire: false,
  pendingMessage: null,
  trackGenerationParams: null,
  suggestions: [],
  suggestionCount: 0,
  dailyBriefing: null,
  showDailyBriefing: false,
  briefingDismissedDate: null,
  generatedFlashcards: null,
  generatedQuiz: null,
  generatedStudyGuide: null,
  isGeneratingContent: false,
  isLoading: false,
  isSending: false,
  isParsing: false,
  isCreatingPlan: false,
  error: null,
};

// Thunks

// Check AI health
export const checkAIHealth = createAsyncThunk(
  'ai/checkHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/health');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to check AI health');
    }
  }
);

// Send chat message with streaming
export const sendMessage = createAsyncThunk(
  'ai/sendMessage',
  async (
    {
      message,
      conversationId,
      imageBase64,
      includeContext = true,
      trackParams,
      onChunk,
    }: {
      message: string;
      conversationId?: string;
      imageBase64?: string;
      includeContext?: boolean;
      trackParams?: TrackGenerationParams;
      onChunk?: (chunk: string) => void;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const token = localStorage.getItem('token');
      const baseUrl = import.meta.env.VITE_API_URL || '/api';

      // Use streaming endpoint
      const response = await fetch(`${baseUrl}/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message,
          conversationId,
          imageBase64,
          includeContext,
          trackParams,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let convId = conversationId;

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.type === 'error') {
                  throw new Error(data.error || 'AI generation failed');
                }
                if (data.conversationId) {
                  convId = data.conversationId;
                }
                if (data.content) {
                  fullContent += data.content;
                  // Update streaming message in state
                  dispatch(updateStreamingMessage(fullContent));
                  onChunk?.(data.content);

                  // Parse streaming content for track data
                  const trackParseResult = parseStreamingTrackData(fullContent);
                  if (trackParseResult.isTrackCreation && trackParseResult.data) {
                    dispatch(updateStreamingTrackData(trackParseResult.data));
                  }
                }
                if (data.done || data.type === 'done') {
                  return {
                    conversationId: convId,
                    message: {
                      id: data.messageId || Date.now().toString(),
                      role: 'assistant' as const,
                      content: fullContent,
                    },
                    actionPreview: data.actionPreview,
                  };
                }
              } catch (e) {
                // Skip invalid JSON lines
              }
            }
          }
        }
      }

      return {
        conversationId: convId,
        message: {
          id: Date.now().toString(),
          role: 'assistant' as const,
          content: fullContent,
        },
      };
    } catch (error) {
      return rejectWithValue('Failed to send message');
    }
  }
);

// Fetch conversations
export const fetchConversations = createAsyncThunk(
  'ai/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/conversations');
      return response.data.conversations;
    } catch (error) {
      return rejectWithValue('Failed to fetch conversations');
    }
  }
);

// Fetch single conversation with messages
export const fetchConversation = createAsyncThunk(
  'ai/fetchConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ai/conversations/${conversationId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch conversation');
    }
  }
);

// Delete conversation
export const deleteConversation = createAsyncThunk(
  'ai/deleteConversation',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/ai/conversations/${conversationId}`);
      return conversationId;
    } catch (error) {
      return rejectWithValue('Failed to delete conversation');
    }
  }
);

// Parse image
export const parseImage = createAsyncThunk(
  'ai/parseImage',
  async (
    { imageBase64, contentType = 'course' }: { imageBase64: string; contentType?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/ai/parse-image', { imageBase64, contentType });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to parse image');
    }
  }
);

// Create track from parsed content
export const createTrackFromParsed = createAsyncThunk(
  'ai/createTrackFromParsed',
  async (
    {
      parsedId,
      trackName,
      trackColor,
      trackIcon,
    }: { parsedId: string; trackName?: string; trackColor?: string; trackIcon?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/ai/parse-image/${parsedId}/create-track`, {
        trackName,
        trackColor,
        trackIcon,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to create track');
    }
  }
);

// Create AI plan
export const createAIPlan = createAsyncThunk(
  'ai/createPlan',
  async (
    planData: {
      goalPrompt: string;
      planType?: string;
      experienceLevel?: string;
      availableHoursPerDay?: number;
      targetDuration?: string;
      preferences?: string[];
      storagePreference?: string;
      autoCreate?: boolean;
      showPreview?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/ai/plans', planData);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to create AI plan');
    }
  }
);

// Fetch plans
export const fetchAIPlans = createAsyncThunk(
  'ai/fetchPlans',
  async (status: string | undefined, { rejectWithValue }) => {
    try {
      const url = status ? `/ai/plans?status=${status}` : '/ai/plans';
      const response = await api.get(url);
      return response.data.plans;
    } catch (error) {
      return rejectWithValue('Failed to fetch plans');
    }
  }
);

// Fetch single plan with tasks
export const fetchAIPlan = createAsyncThunk(
  'ai/fetchPlan',
  async (planId: string, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ai/plans/${planId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch plan');
    }
  }
);

// Activate plan
export const activatePlan = createAsyncThunk(
  'ai/activatePlan',
  async (planId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/ai/plans/${planId}/activate`);
      return { planId, ...response.data };
    } catch (error) {
      return rejectWithValue('Failed to activate plan');
    }
  }
);

// Execute plan (create tracks/cards)
export const executePlan = createAsyncThunk(
  'ai/executePlan',
  async (
    {
      planId,
      createAsTrack = true,
      trackName,
      trackColor,
    }: { planId: string; createAsTrack?: boolean; trackName?: string; trackColor?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(`/ai/plans/${planId}/execute`, {
        createAsTrack,
        trackName,
        trackColor,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to execute plan');
    }
  }
);

// Delete plan
export const deleteAIPlan = createAsyncThunk(
  'ai/deletePlan',
  async (planId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/ai/plans/${planId}`);
      return planId;
    } catch (error) {
      return rejectWithValue('Failed to delete plan');
    }
  }
);

// Fine-tune plan
export const fineTunePlan = createAsyncThunk(
  'ai/fineTunePlan',
  async (planId: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/ai/plans/${planId}/fine-tune`);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fine-tune plan');
    }
  }
);

// Fetch preferences
export const fetchAIPreferences = createAsyncThunk(
  'ai/fetchPreferences',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/preferences');
      return response.data.preferences;
    } catch (error) {
      return rejectWithValue('Failed to fetch preferences');
    }
  }
);

// Update preferences
export const updateAIPreferences = createAsyncThunk(
  'ai/updatePreferences',
  async (updates: Partial<AIUserPreferences>, { rejectWithValue }) => {
    try {
      const response = await api.put('/ai/preferences', updates);
      return response.data.preferences;
    } catch (error) {
      return rejectWithValue('Failed to update preferences');
    }
  }
);

// Fetch insights
export const fetchAIInsights = createAsyncThunk(
  'ai/fetchInsights',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/insights');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch insights');
    }
  }
);

// Fetch proactive suggestions
export const fetchSuggestions = createAsyncThunk(
  'ai/fetchSuggestions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/suggestions');
      return response.data.suggestions;
    } catch (error) {
      return rejectWithValue('Failed to fetch suggestions');
    }
  }
);

// Fetch daily briefing
export const fetchDailyBriefing = createAsyncThunk(
  'ai/fetchDailyBriefing',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/ai/daily-briefing');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch daily briefing');
    }
  }
);

// Fetch analytics report
export const fetchAnalyticsReport = createAsyncThunk(
  'ai/fetchAnalyticsReport',
  async (reportType: string = 'weekly', { rejectWithValue }) => {
    try {
      const response = await api.post('/ai/analytics', { reportType });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to fetch analytics report');
    }
  }
);

// Submit message feedback
export const submitMessageFeedback = createAsyncThunk(
  'ai/submitFeedback',
  async (
    { messageId, feedback, comment }: { messageId: string; feedback: 'positive' | 'negative'; comment?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/ai/feedback', { messageId, feedback, comment });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to submit feedback');
    }
  }
);

// Generate flashcards from a track
export const generateFlashcards = createAsyncThunk(
  'ai/generateFlashcards',
  async ({ trackId, count = 10 }: { trackId: string; count?: number }, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai/generate-flashcards', { trackId, count });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to generate flashcards');
    }
  }
);

// Generate quiz from a track
export const generateQuiz = createAsyncThunk(
  'ai/generateQuiz',
  async (
    { trackId, questionCount = 10, difficulty = 'mixed' }: { trackId: string; questionCount?: number; difficulty?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/ai/generate-quiz', { trackId, questionCount, difficulty });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to generate quiz');
    }
  }
);

// Generate study guide from a track
export const generateStudyGuide = createAsyncThunk(
  'ai/generateStudyGuide',
  async ({ trackId, format = 'detailed' }: { trackId: string; format?: 'detailed' | 'cheat-sheet' }, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai/generate-study-guide', { trackId, format });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to generate study guide');
    }
  }
);

// Undo last AI action
export const undoLastAction = createAsyncThunk(
  'ai/undoLastAction',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai/undo');
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to undo action');
    }
  }
);

// Create full hierarchical track from AI preview data
export const createFullTrack = createAsyncThunk(
  'ai/createFullTrack',
  async (trackData: AITrackData, { rejectWithValue }) => {
    try {
      const response = await api.post('/ai/create-track', {
        trackName: trackData.trackName,
        trackColor: trackData.trackColor,
        trackIcon: trackData.trackIcon,
        trackType: trackData.trackType,
        category: trackData.category,
        description: trackData.description,
        maps: trackData.maps,
        cards: trackData.cards,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to create track');
    }
  }
);

// Slice
const aiSlice = createSlice({
  name: 'ai',
  initialState,
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
    },
    openChat: (state) => {
      state.isOpen = true;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    setCurrentConversation: (state, action: PayloadAction<string | null>) => {
      state.currentConversationId = action.payload;
      if (!action.payload) {
        state.messages = [];
      }
    },
    addUserMessage: (state, action: PayloadAction<{ content: string; imageUrl?: string }>) => {
      state.messages.push({
        id: `temp-${Date.now()}`,
        role: 'user',
        content: action.payload.content,
        imageUrl: action.payload.imageUrl,
        createdAt: new Date().toISOString(),
      });
    },
    setTyping: (state, action: PayloadAction<boolean>) => {
      state.isTyping = action.payload;
    },
    appendStreamingContent: (state, action: PayloadAction<string>) => {
      state.streamingContent += action.payload;
    },
    updateStreamingMessage: (state, action: PayloadAction<string>) => {
      state.streamingContent = action.payload;
    },
    clearStreamingContent: (state) => {
      state.streamingContent = '';
      state.streamingTrackData = null;
      state.isStreamingTrack = false;
    },
    updateStreamingTrackData: (state, action: PayloadAction<AITrackData | null>) => {
      state.streamingTrackData = action.payload;
      state.isStreamingTrack = !!action.payload;
    },
    clearStreamingTrackData: (state) => {
      state.streamingTrackData = null;
      state.isStreamingTrack = false;
    },
    clearParsedContent: (state) => {
      state.parsedContent = null;
      state.parsedContentId = null;
    },
    clearActionPreview: (state) => {
      state.actionPreview = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    startNewConversation: (state) => {
      state.currentConversationId = null;
      state.messages = [];
    },
    showQuestionnaireForMessage: (state, action: PayloadAction<string>) => {
      state.showQuestionnaire = true;
      state.pendingMessage = action.payload;
    },
    hideQuestionnaire: (state) => {
      state.showQuestionnaire = false;
      state.pendingMessage = null;
      state.trackGenerationParams = null;
    },
    setTrackGenerationParams: (state, action: PayloadAction<TrackGenerationParams>) => {
      state.trackGenerationParams = action.payload;
    },
    dismissDailyBriefing: (state) => {
      state.showDailyBriefing = false;
      state.briefingDismissedDate = new Date().toISOString().split('T')[0];
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
      state.suggestionCount = 0;
    },
    clearGeneratedContent: (state) => {
      state.generatedFlashcards = null;
      state.generatedQuiz = null;
      state.generatedStudyGuide = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check health
      .addCase(checkAIHealth.fulfilled, (state, action) => {
        state.health = action.payload;
      })

      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.isSending = true;
        state.isTyping = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.isSending = false;
        state.isTyping = false;
        state.currentConversationId = action.payload.conversationId ?? null;
        state.messages.push({
          id: action.payload.message.id,
          role: 'assistant',
          content: action.payload.message.content,
          createdAt: new Date().toISOString(),
        });

        // Use streaming track data to create action preview for user confirmation
        // This is more reliable than backend regex parsing
        const trackData = state.streamingTrackData;
        if (trackData && (trackData.trackName || trackData.maps?.length || trackData.cards?.length)) {
          // Calculate total cards and minutes
          let totalCards = 0;
          let totalMinutes = 0;

          if (trackData.trackType === 'hierarchical' && trackData.maps) {
            trackData.maps.forEach(map => {
              map.lanes?.forEach(lane => {
                lane.patterns?.forEach(pattern => {
                  totalCards += pattern.cards?.length || 0;
                  pattern.cards?.forEach(card => {
                    totalMinutes += card.estimatedMinutes || 30;
                  });
                });
              });
            });
          } else if (trackData.cards) {
            totalCards = trackData.cards.length;
            trackData.cards.forEach(card => {
              totalMinutes += card.estimatedMinutes || 30;
            });
          }

          if (totalCards > 0 || trackData.trackName) {
            state.actionPreview = {
              type: 'create_track',
              data: {
                ...trackData,
                totalCards,
                totalMinutes,
              },
            };
          }
        } else if (action.payload.actionPreview) {
          // Fallback to backend-provided actionPreview
          state.actionPreview = action.payload.actionPreview;
        }

        // Clear streaming track data since response is complete
        state.streamingTrackData = null;
        state.isStreamingTrack = false;
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        state.isTyping = false;
        state.error = action.payload as string;
      })

      // Fetch conversations
      .addCase(fetchConversations.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch conversation
      .addCase(fetchConversation.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentConversationId = action.payload.conversation.id;
        state.messages = action.payload.messages;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete conversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        state.conversations = state.conversations.filter((c) => c.id !== action.payload);
        if (state.currentConversationId === action.payload) {
          state.currentConversationId = null;
          state.messages = [];
        }
      })

      // Parse image
      .addCase(parseImage.pending, (state) => {
        state.isParsing = true;
        state.error = null;
      })
      .addCase(parseImage.fulfilled, (state, action) => {
        state.isParsing = false;
        state.parsedContent = action.payload.structure;
        state.parsedContentId = action.payload.parsedId;
      })
      .addCase(parseImage.rejected, (state, action) => {
        state.isParsing = false;
        state.error = action.payload as string;
      })

      // Create track from parsed
      .addCase(createTrackFromParsed.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createTrackFromParsed.fulfilled, (state) => {
        state.isLoading = false;
        state.parsedContent = null;
        state.parsedContentId = null;
      })
      .addCase(createTrackFromParsed.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create plan
      .addCase(createAIPlan.pending, (state) => {
        state.isCreatingPlan = true;
        state.error = null;
      })
      .addCase(createAIPlan.fulfilled, (state, action) => {
        state.isCreatingPlan = false;
        state.currentPlan = action.payload.plan;
        state.planTasks = action.payload.tasks;
        state.plans.unshift(action.payload.plan);
      })
      .addCase(createAIPlan.rejected, (state, action) => {
        state.isCreatingPlan = false;
        state.error = action.payload as string;
      })

      // Fetch plans
      .addCase(fetchAIPlans.fulfilled, (state, action) => {
        state.plans = action.payload;
      })

      // Fetch plan
      .addCase(fetchAIPlan.fulfilled, (state, action) => {
        state.currentPlan = action.payload.plan;
        state.planTasks = action.payload.tasks;
      })

      // Activate plan
      .addCase(activatePlan.fulfilled, (state, action) => {
        const index = state.plans.findIndex((p) => p.id === action.payload.planId);
        if (index !== -1) {
          state.plans[index].status = 'active';
        }
        if (state.currentPlan && state.currentPlan.id === action.payload.planId) {
          state.currentPlan.status = 'active';
        }
      })

      // Execute plan
      .addCase(executePlan.fulfilled, (state, action) => {
        const planId = action.meta.arg.planId;
        const index = state.plans.findIndex((p) => p.id === planId);
        if (index !== -1) {
          state.plans[index].status = 'active';
        }
        if (state.currentPlan?.id === planId) {
          state.currentPlan.status = 'active';
        }
      })

      // Delete plan
      .addCase(deleteAIPlan.fulfilled, (state, action) => {
        state.plans = state.plans.filter((p) => p.id !== action.payload);
        if (state.currentPlan?.id === action.payload) {
          state.currentPlan = null;
          state.planTasks = [];
        }
      })

      // Fetch preferences
      .addCase(fetchAIPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
      })

      // Update preferences
      .addCase(updateAIPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
      })

      // Fetch insights
      .addCase(fetchAIInsights.fulfilled, (state, action) => {
        state.insights = action.payload;
      })

      // Fetch suggestions
      .addCase(fetchSuggestions.fulfilled, (state, action) => {
        state.suggestions = action.payload;
        state.suggestionCount = action.payload.length;
      })

      // Fetch daily briefing
      .addCase(fetchDailyBriefing.fulfilled, (state, action) => {
        state.dailyBriefing = action.payload;
        const today = new Date().toISOString().split('T')[0];
        if (state.briefingDismissedDate !== today) {
          state.showDailyBriefing = true;
        }
      })

      // Generate flashcards
      .addCase(generateFlashcards.pending, (state) => {
        state.isGeneratingContent = true;
        state.generatedFlashcards = null;
      })
      .addCase(generateFlashcards.fulfilled, (state, action) => {
        state.isGeneratingContent = false;
        state.generatedFlashcards = action.payload.flashcards;
      })
      .addCase(generateFlashcards.rejected, (state) => {
        state.isGeneratingContent = false;
      })

      // Generate quiz
      .addCase(generateQuiz.pending, (state) => {
        state.isGeneratingContent = true;
        state.generatedQuiz = null;
      })
      .addCase(generateQuiz.fulfilled, (state, action) => {
        state.isGeneratingContent = false;
        state.generatedQuiz = action.payload.questions;
      })
      .addCase(generateQuiz.rejected, (state) => {
        state.isGeneratingContent = false;
      })

      // Generate study guide
      .addCase(generateStudyGuide.pending, (state) => {
        state.isGeneratingContent = true;
        state.generatedStudyGuide = null;
      })
      .addCase(generateStudyGuide.fulfilled, (state, action) => {
        state.isGeneratingContent = false;
        state.generatedStudyGuide = { content: action.payload.content, format: action.payload.format };
      })
      .addCase(generateStudyGuide.rejected, (state) => {
        state.isGeneratingContent = false;
      })

      // Create full hierarchical track
      .addCase(createFullTrack.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFullTrack.fulfilled, (state, action) => {
        state.isLoading = false;
        state.lastActionResult = {
          action: 'create_track',
          success: true,
          trackId: action.payload.trackId,
          trackName: action.payload.trackName,
          cardsCreated: action.payload.totalCards,
          message: action.payload.message,
        };
        // Clear action preview and streaming data after successful creation
        state.actionPreview = null;
        state.streamingTrackData = null;
        state.isStreamingTrack = false;
      })
      .addCase(createFullTrack.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  toggleChat,
  openChat,
  closeChat,
  setCurrentConversation,
  addUserMessage,
  setTyping,
  appendStreamingContent,
  updateStreamingMessage,
  clearStreamingContent,
  updateStreamingTrackData,
  clearStreamingTrackData,
  clearParsedContent,
  clearActionPreview,
  clearError,
  startNewConversation,
  showQuestionnaireForMessage,
  hideQuestionnaire,
  setTrackGenerationParams,
  dismissDailyBriefing,
  clearSuggestions,
  clearGeneratedContent,
} = aiSlice.actions;

export default aiSlice.reducer;
